import { get } from "./apiService";

const cache = {
  allAssets: null,
  walletAssets: null,
  assetPairs: new Map(),
  assetChains: new Map(),
  allChains: null,
  cacheTimestamp: new Map(),
  dynamicCache: new Map(),
};

const CACHE_DURATION = 5 * 60 * 1000;

const pendingRequests = new Map();
const creatingPromises = new Set();

const VALID_STATIC_CACHE_KEYS = ["allAssets", "walletAssets", "allChains"];

const isCacheValid = (key) => {
  const timestamp = cache.cacheTimestamp.get(key);
  if (!timestamp) return false;
  return Date.now() - timestamp < CACHE_DURATION;
};

const setCache = (key, value) => {
  if (key && typeof key === "string") {
    if (VALID_STATIC_CACHE_KEYS.includes(key)) {
      if (key === "allAssets") {
        cache.allAssets = value;
      } else if (key === "walletAssets") {
        cache.walletAssets = value;
      } else if (key === "allChains") {
        cache.allChains = value;
      }
    } else {
      cache.dynamicCache.set(key, value);
    }
  }
  cache.cacheTimestamp.set(key, Date.now());
};

const transformAsset = (asset) => {
  if (!asset) return asset;

  const transformed = { ...asset };

  if (!transformed.assetIcon) {
    if (asset.cmc?.logo) {
      transformed.assetIcon = asset.cmc.logo;
    } else if (asset.image) {
      transformed.assetIcon = asset.image;
    }
  }

  if (!transformed.assetId && transformed._id) {
    transformed.assetId = transformed._id;
  }

  return transformed;
};

const extractAndCachePairs = (assets) => {
  if (!assets || !Array.isArray(assets)) return;

  const assetsMap = new Map();
  assets.forEach((asset) => {
    if (asset._id) {
      assetsMap.set(asset._id, asset);
    }
    if (asset.assetId) {
      assetsMap.set(asset.assetId, asset);
    }
  });

  assets.forEach((asset) => {
    const assetId = asset._id || asset.assetId;
    if (assetId && asset.pairs && Array.isArray(asset.pairs)) {
      const transformedPairs = asset.pairs.map((pair) => {
        if (pair.assetId && pair.asset && pair.assetName) {
          return {
            ...pair,
            pair: pair.pair || `${asset.asset}-${pair.asset}`.toLowerCase(),
            baseCurrency: assetId,
            quoteCurrency: pair.assetId,
            enabled: pair.postTradeEnabled !== false,
            assetIcon: pair.assetIcon,
            assetName: pair.assetName,
            asset: pair.asset,
            minBuyAmountTail: pair.minBuyAmountTail,
            minSellAmountTail: pair.minSellAmountTail,
            postTradeEnabled: pair.postTradeEnabled,
          };
        } else {
          const quoteCurrencyId =
            typeof pair.quoteCurrency === "string"
              ? pair.quoteCurrency
              : pair.quoteCurrency?._id || pair.quoteCurrency || pair.assetId;

          let pairString = pair.pair;
          if (!pairString && asset.asset) {
            const quoteAsset =
              typeof pair.quoteCurrency === "object" &&
              pair.quoteCurrency?.asset
                ? pair.quoteCurrency.asset
                : pair.asset || "";
            pairString = `${asset.asset}-${quoteAsset}`.toLowerCase();
          }

          const quoteAsset = assetsMap.get(quoteCurrencyId);
          const quoteAssetIcon =
            quoteAsset?.assetIcon ||
            quoteAsset?.cmc?.logo ||
            quoteAsset?.image ||
            pair.assetIcon ||
            null;
          const quoteAssetName =
            quoteAsset?.assetName ||
            quoteAsset?.cmc?.name ||
            pair.assetName ||
            null;
          const quoteAssetSymbol = quoteAsset?.asset || pair.asset || null;

          return {
            ...pair,
            pair: pairString || pair.pair,
            baseCurrency: pair.baseCurrency || assetId,
            quoteCurrency: quoteCurrencyId,
            enabled: pair.enabled !== false,
            assetIcon: quoteAssetIcon,
            assetName: quoteAssetName,
            asset: quoteAssetSymbol,
          };
        }
      });

      cache.assetPairs.set(assetId, transformedPairs);
      setCache(`pairs_${assetId}`, transformedPairs);
    }
  });
};

export const getAllTradingAssets = async (type = null) => {
  const cacheKey = `allAssets_${type || "all"}_yes`;

  if (cache.allAssets && isCacheValid(cacheKey)) {
    if (type && cache.allAssets?.data) {
      const filteredAssets = cache.allAssets.data.filter(
        (asset) => asset.type === type
      );
      return {
        ...cache.allAssets,
        data: filteredAssets,
      };
    }
    return cache.allAssets;
  }

  const requestKey = "/api/v2/trading-assets/get-assets";

  const existingPromise = pendingRequests.get(requestKey);
  if (existingPromise) {
    try {
      const result = await existingPromise;
      if (type && result?.data) {
        const filteredAssets = result.data.filter(
          (asset) => asset.type === type
        );
        return {
          ...result,
          data: filteredAssets,
        };
      }
      return result;
    } catch (err) {
      if (pendingRequests.get(requestKey) === existingPromise) {
        pendingRequests.delete(requestKey);
        creatingPromises.delete(requestKey);
      }
      throw err;
    }
  }

  if (creatingPromises.has(requestKey)) {
    while (
      creatingPromises.has(requestKey) &&
      !pendingRequests.has(requestKey)
    ) {
      await new Promise((resolve) => setTimeout(resolve, 1));
    }
    const retryPromise = pendingRequests.get(requestKey);
    if (retryPromise) {
      try {
        const result = await retryPromise;
        if (type && result?.data) {
          const filteredAssets = result.data.filter(
            (asset) => asset.type === type
          );
          return {
            ...result,
            data: filteredAssets,
          };
        }
        return result;
      } catch (err) {
        if (pendingRequests.get(requestKey) === retryPromise) {
          pendingRequests.delete(requestKey);
          creatingPromises.delete(requestKey);
        }
        throw err;
      }
    }
  }

  creatingPromises.add(requestKey);

  let promiseResolve, promiseReject;
  const placeholderPromise = new Promise((resolve, reject) => {
    promiseResolve = resolve;
    promiseReject = reject;
  });

  pendingRequests.set(requestKey, placeholderPromise);
  creatingPromises.delete(requestKey);

  const promiseCreator = async () => {
    try {
      const response = await get("/api/v2/trading-assets/get-assets");

      if (response?.data?.data) {
        let transformedAssets = response.data.data.map(transformAsset);

        const transformedResponse = {
          ...response.data,
          data: transformedAssets,
        };

        extractAndCachePairs(transformedAssets);

        setCache(cacheKey, transformedResponse);
        cache.allAssets = transformedResponse;

        if (type) {
          const filteredAssets = transformedAssets.filter(
            (asset) => asset.type === type
          );
          return {
            ...transformedResponse,
            data: filteredAssets,
          };
        }

        return transformedResponse;
      }

      return response?.data || response;
    } catch (err) {
      throw new Error(`Could not get trading assets: ${err.message}`);
    }
  };

  promiseCreator()
    .then((result) => {
      if (pendingRequests.get(requestKey) === placeholderPromise) {
        pendingRequests.set(requestKey, Promise.resolve(result));
      }
      promiseResolve(result);
    })
    .catch((err) => {
      if (pendingRequests.get(requestKey) === placeholderPromise) {
        pendingRequests.delete(requestKey);
        creatingPromises.delete(requestKey);
      }
      promiseReject(err);
    });

  const requestPromise = placeholderPromise;

  try {
    const result = await requestPromise;
    return result;
  } catch (err) {
    if (pendingRequests.get(requestKey) === requestPromise) {
      pendingRequests.delete(requestKey);
      creatingPromises.delete(requestKey);
    }
    throw err;
  }
};

export const getWalletAssetsData = async () => {
  const cacheKey = "walletAssets";

  if (cache.walletAssets && isCacheValid(cacheKey)) {
    return cache.walletAssets;
  }

  try {
    const response = await get("/api/v1/trading-wallet/get-assets");

    if (response?.data) {
      setCache(cacheKey, response.data);
      cache.walletAssets = response.data;
      return response.data;
    }

    return response;
  } catch (err) {
    throw new Error(`Could not get wallet assets: ${err.message}`);
  }
};

export const getAllTradingData = async (type = null) => {
  const assetsCacheKey = `allAssets_${type || "all"}_yes`;
  const walletCacheKey = "walletAssets";

  const assetsCached = cache.allAssets && isCacheValid(assetsCacheKey);
  const walletCached = cache.walletAssets && isCacheValid(walletCacheKey);

  if (assetsCached && walletCached) {
    return {
      tradingAssets: cache.allAssets,
      walletAssets: cache.walletAssets,
    };
  }

  try {
    const [tradingAssetsResponse, walletAssetsResponse] = await Promise.all([
      getAllTradingAssets(type),
      get("/api/v1/trading-wallet/get-assets"),
    ]);

    const tradingAssets = tradingAssetsResponse;

    if (tradingAssets && (!cache.allAssets || !isCacheValid(assetsCacheKey))) {
      setCache(assetsCacheKey, tradingAssets);
      cache.allAssets = tradingAssets;
    }

    let walletAssets = null;
    if (walletAssetsResponse?.data) {
      setCache(walletCacheKey, walletAssetsResponse.data);
      cache.walletAssets = walletAssetsResponse.data;
      walletAssets = walletAssetsResponse.data;
    } else {
      walletAssets = walletAssetsResponse;
    }

    return {
      tradingAssets,
      walletAssets,
    };
  } catch (err) {
    throw new Error(`Could not get trading data: ${err.message}`);
  }
};

export const getAssetPairs = async (assetId) => {
  if (!assetId) return [];

  const cacheKey = `pairs_${assetId}`;

  if (cache.assetPairs.has(assetId) && isCacheValid(cacheKey)) {
    return cache.assetPairs.get(assetId);
  }

  try {
    if (!cache.allAssets || !isCacheValid("allAssets_all_yes")) {
      await getAllTradingAssets(null);
    }

    if (cache.assetPairs.has(assetId)) {
      return cache.assetPairs.get(assetId);
    }

    const assetsData = cache.allAssets?.data || [];
    const asset = assetsData.find(
      (a) => a._id === assetId || a.assetId === assetId
    );

    if (asset && asset.pairs) {
      const assetsMap = new Map();
      assetsData.forEach((a) => {
        if (a._id) {
          assetsMap.set(a._id, a);
        }
        if (a.assetId) {
          assetsMap.set(a.assetId, a);
        }
      });

      const transformedPairs = asset.pairs.map((pair) => {
        if (pair.assetId && pair.asset && pair.assetName) {
          return {
            ...pair,
            pair: pair.pair || `${asset.asset}-${pair.asset}`.toLowerCase(),
            baseCurrency: assetId,
            quoteCurrency: pair.assetId,
            enabled: pair.postTradeEnabled !== false,
            assetIcon: pair.assetIcon,
            assetName: pair.assetName,
            asset: pair.asset,
            minBuyAmountTail: pair.minBuyAmountTail,
            minSellAmountTail: pair.minSellAmountTail,
            postTradeEnabled: pair.postTradeEnabled,
          };
        } else {
          const quoteCurrencyId =
            typeof pair.quoteCurrency === "string"
              ? pair.quoteCurrency
              : pair.quoteCurrency?._id || pair.quoteCurrency || pair.assetId;

          let pairString = pair.pair;
          if (!pairString && asset.asset) {
            const quoteAsset =
              typeof pair.quoteCurrency === "object" &&
              pair.quoteCurrency?.asset
                ? pair.quoteCurrency.asset
                : pair.asset || "";
            pairString = `${asset.asset}-${quoteAsset}`.toLowerCase();
          }

          const quoteAsset = assetsMap.get(quoteCurrencyId);
          const quoteAssetIcon =
            quoteAsset?.assetIcon ||
            quoteAsset?.cmc?.logo ||
            quoteAsset?.image ||
            pair.assetIcon ||
            null;
          const quoteAssetName =
            quoteAsset?.assetName ||
            quoteAsset?.cmc?.name ||
            pair.assetName ||
            null;
          const quoteAssetSymbol = quoteAsset?.asset || pair.asset || null;

          return {
            ...pair,
            pair: pairString || pair.pair,
            baseCurrency: pair.baseCurrency || assetId,
            quoteCurrency: quoteCurrencyId,
            enabled: pair.enabled !== false,
            assetIcon: quoteAssetIcon,
            assetName: quoteAssetName,
            asset: quoteAssetSymbol,
          };
        }
      });

      cache.assetPairs.set(assetId, transformedPairs);
      setCache(cacheKey, transformedPairs);
      return transformedPairs;
    }

    return [];
  } catch (err) {
    console.error(`Could not get pairs for asset ${assetId}:`, err);
    return [];
  }
};

export const getAllChains = async () => {
  const cacheKey = "allChains";

  if (cache.allChains && isCacheValid(cacheKey)) {
    return cache.allChains;
  }

  const requestKey = "/api/v2/trading-assets/get-chains";

  const existingPromise = pendingRequests.get(requestKey);
  if (existingPromise) {
    try {
      const result = await existingPromise;
      return result;
    } catch (err) {
      if (pendingRequests.get(requestKey) === existingPromise) {
        pendingRequests.delete(requestKey);
        creatingPromises.delete(requestKey);
      }
      throw err;
    }
  }

  if (creatingPromises.has(requestKey)) {
    while (
      creatingPromises.has(requestKey) &&
      !pendingRequests.has(requestKey)
    ) {
      await new Promise((resolve) => setTimeout(resolve, 1));
    }
    const retryPromise = pendingRequests.get(requestKey);
    if (retryPromise) {
      try {
        const result = await retryPromise;
        return result;
      } catch (err) {
        if (pendingRequests.get(requestKey) === retryPromise) {
          pendingRequests.delete(requestKey);
          creatingPromises.delete(requestKey);
        }
        throw err;
      }
    }
  }

  creatingPromises.add(requestKey);

  let promiseResolve, promiseReject;
  const placeholderPromise = new Promise((resolve, reject) => {
    promiseResolve = resolve;
    promiseReject = reject;
  });

  pendingRequests.set(requestKey, placeholderPromise);
  creatingPromises.delete(requestKey);

  const promiseCreator = async () => {
    try {
      const response = await get("/api/v2/trading-assets/get-chains");

      if (response?.data?.data) {
        cache.allChains = response.data.data;
        setCache(cacheKey, response.data.data);
        return response.data.data;
      }

      return response?.data?.data || [];
    } catch (err) {
      console.error("Could not get all chains:", err);
      throw new Error(`Could not get all chains: ${err.message}`);
    }
  };

  promiseCreator()
    .then((result) => {
      if (pendingRequests.get(requestKey) === placeholderPromise) {
        pendingRequests.set(requestKey, Promise.resolve(result));
      }
      promiseResolve(result);
    })
    .catch((err) => {
      if (pendingRequests.get(requestKey) === placeholderPromise) {
        pendingRequests.delete(requestKey);
        creatingPromises.delete(requestKey);
      }
      promiseReject(err);
    });

  const requestPromise = placeholderPromise;

  try {
    const result = await requestPromise;
    return result;
  } catch (err) {
    if (pendingRequests.get(requestKey) === requestPromise) {
      pendingRequests.delete(requestKey);
      creatingPromises.delete(requestKey);
    }
    return [];
  }
};

export const getAssetChains = async (assetId) => {
  if (!assetId) return null;

  const cacheKey = `chains_${assetId}`;

  if (cache.assetChains.has(assetId) && isCacheValid(cacheKey)) {
    return cache.assetChains.get(assetId);
  }

  try {
    if (!cache.allChains || !isCacheValid("allChains")) {
      await getAllChains();
    }

    const filteredChains = (cache.allChains || []).filter(
      (chain) => chain.assetId === assetId
    );

    if (filteredChains.length > 0) {
      cache.assetChains.set(assetId, filteredChains);
      setCache(cacheKey, filteredChains);
      return filteredChains;
    }

    return [];
  } catch (err) {
    console.error(`Could not get chains for asset ${assetId}:`, err);
    return [];
  }
};

export const batchGetAssetPairs = async (assetIds) => {
  if (!assetIds || assetIds.length === 0) return new Map();

  if (!cache.allAssets || !isCacheValid("allAssets_all_yes")) {
    await getAllTradingAssets(null);
  }

  const result = new Map();

  assetIds.forEach((assetId) => {
    if (cache.assetPairs.has(assetId)) {
      result.set(assetId, cache.assetPairs.get(assetId));
    } else {
      const assetsData = cache.allAssets?.data || [];
      const asset = assetsData.find(
        (a) => a._id === assetId || a.assetId === assetId
      );

      if (asset && asset.pairs) {
        const assetsMap = new Map();
        assetsData.forEach((a) => {
          if (a._id) {
            assetsMap.set(a._id, a);
          }
          if (a.assetId) {
            assetsMap.set(a.assetId, a);
          }
        });

        const transformedPairs = asset.pairs.map((pair) => {
          if (pair.assetId && pair.asset && pair.assetName) {
            return {
              ...pair,
              pair: pair.pair || `${asset.asset}-${pair.asset}`.toLowerCase(),
              baseCurrency: assetId,
              quoteCurrency: pair.assetId,
              enabled: pair.postTradeEnabled !== false,
              assetIcon: pair.assetIcon,
              assetName: pair.assetName,
              asset: pair.asset,
              minBuyAmountTail: pair.minBuyAmountTail,
              minSellAmountTail: pair.minSellAmountTail,
              postTradeEnabled: pair.postTradeEnabled,
            };
          } else {
            const quoteCurrencyId =
              typeof pair.quoteCurrency === "string"
                ? pair.quoteCurrency
                : pair.quoteCurrency?._id || pair.quoteCurrency || pair.assetId;

            let pairString = pair.pair;
            if (!pairString && asset.asset) {
              const quoteAsset =
                typeof pair.quoteCurrency === "object" &&
                pair.quoteCurrency?.asset
                  ? pair.quoteCurrency.asset
                  : pair.asset || "";
              pairString = `${asset.asset}-${quoteAsset}`.toLowerCase();
            }

            const quoteAsset = assetsMap.get(quoteCurrencyId);
            const quoteAssetIcon =
              quoteAsset?.assetIcon ||
              quoteAsset?.cmc?.logo ||
              quoteAsset?.image ||
              pair.assetIcon ||
              null;
            const quoteAssetName =
              quoteAsset?.assetName ||
              quoteAsset?.cmc?.name ||
              pair.assetName ||
              null;
            const quoteAssetSymbol = quoteAsset?.asset || pair.asset || null;

            return {
              ...pair,
              pair: pairString || pair.pair,
              baseCurrency: pair.baseCurrency || assetId,
              quoteCurrency: quoteCurrencyId,
              enabled: pair.enabled !== false,
              assetIcon: quoteAssetIcon,
              assetName: quoteAssetName,
              asset: quoteAssetSymbol,
            };
          }
        });

        cache.assetPairs.set(assetId, transformedPairs);
        setCache(`pairs_${assetId}`, transformedPairs);
        result.set(assetId, transformedPairs);
      } else {
        result.set(assetId, []);
      }
    }
  });

  return result;
};

export const getAllTradablePairsOptimized = async () => {
  try {
    const cacheKey = `allAssets_Crypto_yes`;
    if (cache.allAssets && isCacheValid(cacheKey)) {
      const allCryptoAssets = cache.allAssets?.data || [];

      if (allCryptoAssets.length === 0) {
        return {};
      }

      const assetIds = allCryptoAssets
        .map((asset) => asset._id || asset.assetId)
        .filter(Boolean);
      const pairsMap = await batchGetAssetPairs(assetIds);

      const result = {};
      assetIds.forEach((assetId) => {
        if (pairsMap.has(assetId)) {
          result[String(assetId)] = pairsMap.get(assetId);
        } else {
          result[String(assetId)] = [];
        }
      });

      return result;
    }

    const tradingAssetsResponse = await getAllTradingAssets("Crypto");
    const allCryptoAssets = tradingAssetsResponse?.data || [];

    if (allCryptoAssets.length === 0) {
      return {};
    }

    const assetIds = allCryptoAssets
      .map((asset) => asset._id || asset.assetId)
      .filter(Boolean);
    const pairsMap = await batchGetAssetPairs(assetIds);

    const result = {};
    assetIds.forEach((assetId) => {
      if (pairsMap.has(assetId)) {
        result[String(assetId)] = pairsMap.get(assetId);
      } else {
        result[String(assetId)] = [];
      }
    });

    return result;
  } catch (err) {
    console.error("Failed to get all tradable pairs:", err);
    return {};
  }
};

export const clearCache = (key = null) => {
  if (key) {
    if (key.startsWith("pairs_")) {
      const assetId = key.replace("pairs_", "");
      cache.assetPairs.delete(assetId);
    } else if (key.startsWith("chains_")) {
      const assetId = key.replace("chains_", "");
      cache.assetChains.delete(assetId);
    }
    cache.cacheTimestamp.delete(key);
    if (key && typeof key === "string") {
      if (key === "allAssets") {
        cache.allAssets = null;
      } else if (key === "walletAssets") {
        cache.walletAssets = null;
      } else if (key === "allChains") {
        cache.allChains = null;
      } else if (key.startsWith("allAssets_")) {
        cache.dynamicCache.delete(key);
      } else {
        cache.dynamicCache.delete(key);
      }
    }
  } else {
    cache.allAssets = null;
    cache.walletAssets = null;
    cache.assetPairs.clear();
    cache.assetChains.clear();
    cache.allChains = null;
    cache.dynamicCache.clear();
    cache.cacheTimestamp.clear();
  }
};

export const prefetchCommonAssets = async () => {
  try {
    await getAllTradingData("Crypto");
  } catch (err) {
    console.error("Prefetch failed:", err);
  }
};
